import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import React from 'react';
import '@/App.css';

// Auth & Module Selection
import GalaxyLogin from '@/pages/GalaxyLogin';
import ModuleSelection from '@/pages/ModuleSelection';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import DatabaseMigration from '@/pages/DatabaseMigration';
import UserManagement from '@/pages/UserManagement';
import WhatsAppSettings from '@/pages/WhatsAppSettings';
import WhatsAppInbox from '@/pages/WhatsAppInbox';
import CompanySubscription from '@/pages/CompanySubscription';
import AdminSubscriptionPlans from '@/pages/AdminSubscriptionPlans';

import { initPushNotifications } from '@/utils/pushNotifications';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import ModuleSwitcherBar from '@/components/ModuleSwitcherBar';

// Sales Module Pages
import Dashboard from '@/pages/Dashboard';
import BookingForm from '@/pages/BookingForm';
import EnhancedBookingsList from '@/pages/EnhancedBookingsList';
import MastersManagement from '@/pages/MastersManagement';
import GoogleSheetsSettings from '@/pages/GoogleSheetsSettings';
import BulkUpload from '@/pages/BulkUpload';
import Reports from '@/pages/Reports';
import MISReports from '@/pages/MISReports';
import NumberPlateReports from '@/pages/NumberPlateReports';
import MultiReports from '@/pages/MultiReports';
import StockReport from '@/pages/StockReport';
import DeliveryReport from '@/pages/DeliveryReport';
import RTOReport from '@/pages/RTOReport';
import FinancierReport from '@/pages/FinancierReport';
import ExchangeVehicleReport from '@/pages/ExchangeVehicleReport';
import DatabaseBackup from '@/pages/DatabaseBackup';
import SalesRepPerformance from '@/pages/SalesRepPerformance';
import InsuranceExpiryReport from '@/pages/InsuranceExpiryReport';
import SalesTemplateManagement from '@/pages/SalesTemplateManagement';
import ReportTemplates from '@/pages/ReportTemplates';
import VehiclePriceMaster from '@/pages/VehiclePriceMaster';
import CatalogMaster from '@/pages/CatalogMaster';
import ProformaInvoice from '@/pages/ProformaInvoice';

// Service Module Pages
import ServiceDashboard from '@/pages/ServiceDashboard';
import ServiceJobsList from '@/pages/ServiceJobsList';
import ServiceUpload from '@/pages/ServiceUpload';
import ServiceJobForm from '@/pages/ServiceJobForm';
import ServiceMasters from '@/pages/ServiceMasters';
import ServiceReports from '@/pages/ServiceReports';
import ServiceTemplates from '@/pages/ServiceTemplates';
import ServiceSettings from '@/pages/ServiceSettings';
import ServiceMultiReports from '@/pages/ServiceMultiReports';

// Lead Management Module Pages
import LeadsDashboard from '@/pages/LeadsDashboard';
import KanbanBoard from '@/pages/KanbanBoard';
import LeadEntryForm from '@/pages/LeadEntryForm';
import LeadsList from '@/pages/LeadsList';
import LeadDetailView from '@/pages/LeadDetailView';
import LeadSourceReport from '@/pages/LeadSourceReport';
import LeadRepPerformance from '@/pages/LeadRepPerformance';
import FollowUpDueReport from '@/pages/FollowUpDueReport';
import LostLeadReport from '@/pages/LostLeadReport';
import FinanciersReport from '@/pages/FinanciersReport';
import AutoFollowUpSettings from '@/pages/AutoFollowUpSettings';
import TemplateManagement from '@/pages/TemplateManagement';
import LeadSourceMasters from '@/pages/LeadSourceMasters';
import AuditLogs from '@/pages/AuditLogs';
import DatabaseAdmin from '@/pages/DatabaseAdmin';
import MetaLeadsConfig from '@/pages/MetaLeadsConfig';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TaskBoard from '@/pages/TaskBoard';
import EmailDigestSettings from '@/pages/EmailDigestSettings';

import TaskFAB from '@/components/TaskFAB';
import AIAssistantFAB from '@/components/AIAssistantFAB';
import { Toaster } from '@/components/ui/sonner';

function App() {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const handleLogout = React.useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  React.useEffect(() => {
    // Check for both old 'token' and new 'access_token' keys
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      // Basic JWT expiry check (decode payload without verification)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          // Token expired — clear auth
          handleLogout();
          setLoading(false);
          return;
        }
      } catch { /* If decode fails, let the API handle it */ }
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, [handleLogout]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('token', token); // Keep for backward compatibility
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Initialize push notifications after login
    setTimeout(() => initPushNotifications(), 2000);
  };

  // Also init push on page load if already logged in
  React.useEffect(() => {
    if (user) {
      setTimeout(() => initPushNotifications(), 3000);
    }
  }, [user]);

  // Determine where to redirect after login
  const getDefaultRoute = () => {
    if (!user) return '/';
    if (user.is_super_admin) return '/admin';
    return '/modules';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <PermissionsProvider user={user}>
        <BrowserRouter>
          <ModuleSwitcherBar />
          <Routes>
            {/* Auth Routes */}
            <Route path="/" element={user ? <Navigate to={getDefaultRoute()} /> : <GalaxyLogin onLogin={handleLogin} />} />
            <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} /> : <GalaxyLogin onLogin={handleLogin} />} />
          
          {/* Super Admin Routes */}
          <Route path="/admin" element={
            user?.is_super_admin 
              ? <SuperAdminDashboard user={user} onLogout={handleLogout} /> 
              : <Navigate to="/" />
          } />
          <Route path="/super-admin" element={
            user?.is_super_admin 
              ? <SuperAdminDashboard user={user} onLogout={handleLogout} /> 
              : <Navigate to="/" />
          } />
          <Route path="/admin/migration" element={
            user?.is_super_admin 
              ? <DatabaseMigration user={user} onLogout={handleLogout} /> 
              : <Navigate to="/" />
          } />
          
          {/* Module Selection */}
          <Route path="/modules" element={user ? <ModuleSelection user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* User Management (Company Admins) */}
          <Route path="/settings/users" element={user ? <UserManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* Database Admin Panel (Admin only) */}
          <Route path="/admin/database" element={user?.is_super_admin ? <DatabaseAdmin user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* WhatsApp Settings (Admin only) */}
          <Route path="/settings/whatsapp" element={user ? <WhatsAppSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/whatsapp/inbox" element={user ? <WhatsAppInbox user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/subscription" element={user ? <CompanySubscription user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/admin/subscription-plans" element={user ? <AdminSubscriptionPlans user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* ==================== SALES MODULE ==================== */}
          <Route path="/sales/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/bookings" element={user ? <EnhancedBookingsList user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/bookings/new" element={user ? <BookingForm user={user} onLogout={handleLogout} mode="create" /> : <Navigate to="/" />} />
          <Route path="/sales/bookings/edit/:id" element={user ? <BookingForm user={user} onLogout={handleLogout} mode="edit" /> : <Navigate to="/" />} />
          <Route path="/sales/bookings/:id/edit" element={user ? <BookingForm user={user} onLogout={handleLogout} mode="edit" /> : <Navigate to="/" />} />
          <Route path="/sales/bookings/:id/view" element={user ? <BookingForm user={user} onLogout={handleLogout} mode="view" /> : <Navigate to="/" />} />
          <Route path="/sales/bulk-upload" element={user ? <BulkUpload user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/masters" element={user ? <MastersManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/templates" element={user ? <SalesTemplateManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/report-templates" element={user ? <ReportTemplates user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/reports" element={user ? <Reports user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/mis-reports" element={user ? <MISReports user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/multi-reports" element={user ? <MultiReports user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/stock-report" element={user ? <StockReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/delivery-report" element={user ? <DeliveryReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/rto-report" element={user ? <RTOReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/financier-report" element={user ? <FinancierReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/exchange-vehicle-report" element={user ? <ExchangeVehicleReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/database-backup" element={user?.is_super_admin ? <DatabaseBackup user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/reports/sales-rep-performance" element={user ? <SalesRepPerformance user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/reports/insurance-expiry" element={user ? <InsuranceExpiryReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/google-sheets" element={user ? <GoogleSheetsSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/vehicle-price-master" element={user ? <VehiclePriceMaster user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/catalog-master" element={user ? <CatalogMaster user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/proforma-invoice" element={user ? <ProformaInvoice user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/sales/proforma-invoice/:type/:id" element={user ? <ProformaInvoice user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* Legacy routes - redirect to new paths */}
          <Route path="/dashboard" element={<Navigate to="/sales/dashboard" />} />
          <Route path="/bookings" element={<Navigate to="/sales/bookings" />} />
          <Route path="/bookings/new" element={<Navigate to="/sales/bookings/new" />} />
          <Route path="/bookings/edit/:id" element={<Navigate to="/sales/bookings/edit/:id" />} />
          <Route path="/bookings/:id/edit" element={<Navigate to="/sales/bookings/:id/edit" />} />
          <Route path="/bookings/:id/view" element={<Navigate to="/sales/bookings/:id/view" />} />
          <Route path="/masters" element={<Navigate to="/sales/masters" />} />
          <Route path="/reports" element={<Navigate to="/sales/reports" />} />
          <Route path="/mis-reports" element={<Navigate to="/sales/mis-reports" />} />
          <Route path="/multi-reports" element={<Navigate to="/sales/multi-reports" />} />
          <Route path="/stock-report" element={<Navigate to="/sales/stock-report" />} />
          <Route path="/delivery-report" element={<Navigate to="/sales/delivery-report" />} />
          <Route path="/rto-report" element={<Navigate to="/sales/rto-report" />} />
          <Route path="/financier-report" element={<Navigate to="/sales/financier-report" />} />
          <Route path="/google-sheets" element={<Navigate to="/sales/google-sheets" />} />
          <Route path="/number-plate-reports" element={<Navigate to="/sales/multi-reports" />} />
          <Route path="/bookings/bulk-upload" element={<Navigate to="/sales/bulk-upload" />} />
          
          {/* ==================== SERVICE MODULE ==================== */}
          <Route path="/service/dashboard" element={user ? <ServiceDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/jobs" element={user ? <ServiceJobsList user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/jobs/new" element={user ? <ServiceJobForm user={user} onLogout={handleLogout} mode="create" /> : <Navigate to="/" />} />
          <Route path="/service/jobs/:id/edit" element={user ? <ServiceJobForm user={user} onLogout={handleLogout} mode="edit" /> : <Navigate to="/" />} />
          <Route path="/service/jobs/:id/view" element={user ? <ServiceJobForm user={user} onLogout={handleLogout} mode="view" /> : <Navigate to="/" />} />
          <Route path="/service/upload" element={user ? <ServiceUpload user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/masters" element={user ? <Navigate to="/service/masters/status" /> : <Navigate to="/" />} />
          <Route path="/service/masters/:type" element={user ? <ServiceMasters user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/reports" element={user ? <Navigate to="/service/reports/vehicle-inwards" /> : <Navigate to="/" />} />
          <Route path="/service/reports/multi-reports" element={user ? <ServiceMultiReports user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/reports/:variant" element={user ? <ServiceReports user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/templates" element={user ? <ServiceTemplates user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/service/settings" element={user ? <ServiceSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* ==================== LEAD MANAGEMENT MODULE ==================== */}
          <Route path="/leads" element={user ? <LeadsDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/kanban" element={user ? <KanbanBoard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/list" element={user ? <LeadsList user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/view/:id" element={user ? <LeadDetailView user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/add" element={user ? <LeadEntryForm user={user} onLogout={handleLogout} mode="create" /> : <Navigate to="/" />} />
          <Route path="/leads/edit/:id" element={user ? <LeadEntryForm user={user} onLogout={handleLogout} mode="edit" /> : <Navigate to="/" />} />
          <Route path="/leads/reports/source" element={user ? <LeadSourceReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/reports/rep-performance" element={user ? <LeadRepPerformance user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/reports/follow-up" element={user ? <FollowUpDueReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/reports/lost" element={user ? <LostLeadReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/reports/financiers" element={user ? <FinanciersReport user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/settings/auto-followup" element={user ? <AutoFollowUpSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/settings/templates" element={user ? <TemplateManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/settings/sources" element={user ? <LeadSourceMasters user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/leads/settings/meta-ads" element={user ? <MetaLeadsConfig user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* ==================== AUDIT LOGS ==================== */}
          <Route path="/settings/audit-logs" element={user ? <AuditLogs user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* ==================== TASKS & TO-DO ==================== */}
          <Route path="/tasks" element={user ? <TaskBoard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/tasks/email-digest" element={user ? <EmailDigestSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
          
          {/* ==================== PUBLIC PAGES ==================== */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
        {/* Global Task FAB — visible on all authenticated pages */}
        {user && <TaskFAB user={user} />}
        {/* Global AI Assistant FAB — Company Admin only (no other role can see/use AI) */}
        {user && user.role_name === 'Company Admin' && <AIAssistantFAB user={user} />}
        </BrowserRouter>
      </PermissionsProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
